<template>
  <div :class="['blok-table', { alternate }]">
    <div ref="tableContainer" class="table-wrapper" @scroll="handleScroll">
      <table>
        <tbody>
          <tr v-for="(row, rowIndex) in tableRows" :key="rowIndex">
            <td
              v-for="(cell, cellIndex) in row"
              :key="cellIndex"
              :colspan="cell.colspan > 1 ? cell.colspan : undefined"
              :rowspan="cell.rowspan > 1 ? cell.rowspan : undefined"
              :style="cell.backgroundColor ? `background-color: ${cell.backgroundColor}` : undefined"
            >
              <div v-html="renderCellContent(cell.content)"></div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Navigation controls for mobile -->
    <div class="table-navigation">
      <div class="table-pagination">
        <div class="pagination-track">
          <div class="pagination-indicator" :style="{ width: indicatorWidth, transform: `translateX(${indicatorPosition}px)` }"></div>
        </div>
      </div>
      <div class="table-nav-buttons">
        <button
          class="table-nav-button table-nav-prev"
          :class="{ disabled: isAtStart }"
          :disabled="isAtStart"
          @click="scrollLeft"
          aria-label="Précédent"
        ></button>
        <button
          class="table-nav-button table-nav-next"
          :class="{ disabled: isAtEnd }"
          :disabled="isAtEnd"
          @click="scrollRight"
          aria-label="Suivant"
        ></button>
      </div>
    </div>
  </div>
</template>

<script setup>
const props = defineProps({
  blok: {
    type: Object,
    required: true,
  },
  alternate: {
    type: Boolean,
    default: false,
  }
});

const { blok } = toRefs(props);

const tableContainer = ref(null);
const isAtStart = ref(true);
const isAtEnd = ref(false);
const indicatorWidth = ref('33%');
const indicatorPosition = ref(0);

// Parse table structure from Storyblok rich text format
const tableRows = computed(() => {
  const content = blok.value.content;

  if (!content || content.type !== 'table') {
    return [];
  }

  return content.content.map(row => {
    if (row.type !== 'tableRow') return [];

    return row.content.map(cell => ({
      colspan: cell.attrs?.colspan || 1,
      rowspan: cell.attrs?.rowspan || 1,
      backgroundColor: cell.attrs?.backgroundColor || null,
      content: cell.content || []
    }));
  });
});

const renderCellContent = (content) => {
  if (!content?.length) return '';

  return content.map(item => {
    if (item.type !== 'paragraph') return '';

    const text = item.content?.map(node => {
      if (node.type !== 'text') return '';

      let html = node.text || '';

      node.marks?.forEach(mark => {
        if (mark.type === 'bold') html = `<strong>${html}</strong>`;
        else if (mark.type === 'italic') html = `<em>${html}</em>`;
        else if (mark.type === 'textStyle' && mark.attrs?.color) {
          html = `<span style="color: ${mark.attrs.color}">${html}</span>`;
        }
      });

      return html;
    }).join('');

    return text ? `<p style="line-height: 150% !important;">${text}</p>` : '';
  }).join('');
};

// Scroll handling
const handleScroll = () => {
  if (!tableContainer.value) return;

  const container = tableContainer.value;
  const scrollLeft = container.scrollLeft;
  const scrollWidth = container.scrollWidth;
  const clientWidth = container.clientWidth;

  // Check if at start or end
  isAtStart.value = scrollLeft <= 0;
  isAtEnd.value = scrollLeft + clientWidth >= scrollWidth - 1;

  // Update indicator position
  const maxScroll = scrollWidth - clientWidth;
  if (maxScroll > 0) {
    const scrollPercent = scrollLeft / maxScroll;
    const trackWidth = container.clientWidth;
    const indicatorWidthPx = (trackWidth * parseFloat(indicatorWidth.value)) / 100;
    const maxIndicatorPosition = trackWidth - indicatorWidthPx;
    indicatorPosition.value = scrollPercent * maxIndicatorPosition;
  } else {
    indicatorPosition.value = 0;
  }
};

const scroll = (direction) => {
  if (!tableContainer.value) return;
  const scrollAmount = tableContainer.value.clientWidth * 0.8 * direction;
  tableContainer.value.scrollBy({ left: scrollAmount, behavior: 'smooth' });
};

const scrollLeft = () => scroll(-1);
const scrollRight = () => scroll(1);

onMounted(() => {
  if (tableContainer.value) {
    const container = tableContainer.value;
    const visibleRatio = container.clientWidth / container.scrollWidth;
    indicatorWidth.value = `${Math.max(20, visibleRatio * 100)}%`;
    handleScroll();
  }
});
</script>

<style lang="scss">
.blok-table {
  width: 100%;
  margin: 2rem 0;
  position: relative;
  padding: 0 1rem;

  .table-wrapper {
    width: 100%;
    overflow-x: auto;
    scroll-behavior: smooth;
    -webkit-overflow-scrolling: touch;

    &::-webkit-scrollbar {
      display: none;
    }
    -ms-overflow-style: none;
    scrollbar-width: none;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    background-color: transparent;
    border-radius: 20px;
    overflow: hidden;

    tbody {
      @include pair-blok-table;
      
      tr {
        &:nth-child(odd) {
          background-color: var(--solid-02, #fafafa);
        }

        &:nth-child(even) {
          background-color: var(--solid-01);
        }

        td {
          padding: 0.75rem 1.5rem;
          border: none;
          text-align: left;
          font-size: 16px;

          :deep(p) {
            margin: 0 !important;
            font-size: 16px !important;
            color: var(--text-100, #111);
            text-align: left;

            &:not(:last-child) {
              margin-bottom: 0.5rem;
            }
          }

          :deep(strong) {
            font-weight: 600;
          }

          :deep(em) {
            font-style: italic;
          }
        }

        &:first-child td {
          font-weight: 700;
          padding: 1rem 1.5rem;
          font-size: 18px;

          :deep(p) {
            font-size: 18px !important;
          }
        }

        &:not(:first-child) td {
          font-size: 16px;

          :deep(p) {
            font-size: 16px !important;
          }
        }
      }
    }
  }

  .table-navigation {
    display: none;
  }

  @include for-tablet-portrait-up {
    table {
      min-width: 100%;
    }
  }

  @include for-phone-only {
    width: calc(100% + 5rem);
    margin-left: -2.5rem;
    padding-bottom: 4rem;

    .table-wrapper {
      overflow-x: auto;
      padding-left: 1.125rem;
      padding-right: 1rem;
    }

    table {
      min-width: 600px;

      td {
        padding: 0.5rem 0.75rem;

        :deep(p) {
          font-size: 14px;
        }
      }

      tr:first-child td {
        padding: 0.75rem 0.75rem;
      }
    }

    .table-navigation {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      margin-top: 1.5rem;
    }

    .table-pagination {
      width: 100%;
      display: flex;
      align-items: center;
      padding: 0 1.125rem;
    }

    .pagination-track {
      width: 100%;
      height: 0.25rem;
      background-color: rgba(0, 0, 0, 0.1);
      border-radius: 0.125rem;
      position: relative;
      overflow: hidden;
    }

    .pagination-indicator {
      height: 100%;
      background: linear-gradient(
        90deg,
        var(--gradient-alt-start) 0%,
        var(--gradient-alt-end) 100%
      );
      border-radius: 0.125rem;
      transition: transform 0.1s ease-out;
    }

    .table-nav-buttons {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 0.75rem;
      padding: 0 1.125rem;
    }

    .table-nav-button {
      width: 3.25rem;
      height: 3.25rem;
      border-radius: 50%;
      background: linear-gradient(
        90deg,
        var(--gradient-alt-start) 0%,
        var(--gradient-alt-end) 100%
      );
      border: none;
      cursor: pointer;
      position: relative;
      flex-shrink: 0;

      &::before {
        content: "";
        width: 3rem;
        height: 3rem;
        border-radius: 50%;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate3d(-50%, -50%, 0);
        background-color: var(--solid-07);
      }

      &::after {
        content: '';
        width: 3.25rem;
        height: 3.25rem;
        background-position: center center;
        background-repeat: no-repeat;
        position: absolute;
        top: 0;
        left: 0;
        z-index: 12;
      }

      &.disabled {
        opacity: 0.3;
        cursor: not-allowed;
      }
    }

    .table-nav-prev::after {
      background-image: url("~/assets/svg/func_left.svg");
    }

    .table-nav-next::after {
      background-image: url("~/assets/svg/func_right.svg");
    }
  }
}

.blok-table {
  &.alternate {
    table {
      tbody {
        @include pair-blok-table-alternate;

        tr:nth-child(even) {
          background-color: white; 
        }
      }
    }
  }
}
</style>